import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiSettings, FiLogOut, FiHelpCircle, FiSliders, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../context/ToastContext';
import s from './FloatingButtons.module.css';

// Format a snake_case market-group code into a readable label
// e.g. corners_ft_ou -> 'Corners FT O/U'
function formatMarketGroupLabel(code) {
  if (!code) return '';
  const parts = String(code).split('_');
  const map = {
    ft: 'FT', ht: 'HT', sh: '2H',
    '1x2': '1X2', dc: 'DC', ou: 'O/U', eh: 'EH', et: 'ET', bt: 'BT',
    btts: 'BTTS', fg: 'First Goal', hsh: 'HSH', wtn: 'WTN', dnb: 'DNB',
    yc: 'YC', sot: 'SOT', corners: 'Corners',
  };
  return parts.map(p => map[p] || (p.charAt(0).toUpperCase() + p.slice(1))).join(' ');
}

export default function FloatingProfileButton() {
  const { user, logout } = useAuth();
  const api = useApi();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [marketsOpen, setMarketsOpen] = useState(false);
  const [marketGroups, setMarketGroups] = useState([]); // [{ code, label }]
  const [marketStates, setMarketStates] = useState({});
  const [allKill, setAllKill] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setMarketsOpen(false); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch dynamic market groups when the controls panel is opened
  useEffect(() => {
    if (!marketsOpen || marketGroups.length > 0) return;
    let cancelled = false;
    api.get('/v1/clasico/market-groups').then(data => {
      if (cancelled || !data) return;
      const merged = { ...(data.matchMarketGroups || {}), ...(data.teamMarketGroups || {}) };
      const codes = Object.keys(merged);
      if (codes.length === 0) return;
      const groups = codes.map(code => ({ code, label: formatMarketGroupLabel(code) }));
      setMarketGroups(groups);
      setMarketStates(prev => {
        const next = { ...prev };
        groups.forEach(g => { if (next[g.code] == null) next[g.code] = true; });
        return next;
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [marketsOpen, marketGroups.length, api]);

  const handleOpenMarkets = () => {
    setMarketsOpen(!marketsOpen);
  };

  const handleToggleMarketGroup = async (groupCode) => {
    const newVal = !marketStates[groupCode];
    try {
      await api.put('/v1/clasico/bulk/markets', {
        markets: { [groupCode]: newVal },
      });
      setMarketStates(prev => ({ ...prev, [groupCode]: newVal }));
      toast.success(`${groupCode.replace(/_/g, ' ')} ${newVal ? 'enabled' : 'disabled'} for all pending/started`);
    } catch (err) {
      toast.error(err.message || 'Failed to toggle market');
    }
  };

  const handleDisableAll = async () => {
    try {
      await api.put('/v1/clasico/bulk/markets', { allDisabled: true });
      setAllKill(true);
      toast.success('All markets disabled for all pending/started clasicos');
    } catch (err) {
      toast.error(err.message || 'Failed to disable all');
    }
  };

  const handleEnableAll = async () => {
    try {
      await api.put('/v1/clasico/bulk/markets', { allDisabled: false });
      setAllKill(false);
      toast.success('All markets re-enabled for all pending/started clasicos');
    } catch (err) {
      toast.error(err.message || 'Failed to enable all');
    }
  };

  return (
    <div className={`${s.fab} ${s.fabRight}`} ref={ref}>
      <button className={s.button} onClick={() => { setOpen(!open); if (open) setMarketsOpen(false); }}>
        <img className={s.avatar} src={process.env.PUBLIC_URL + '/clasico_analytics.jpeg'} alt="Profile" />
      </button>

      {open && !marketsOpen && (
        <div className={`${s.menu} ${s.menuRight}`}>
          <div className={s.menuHeader}>
            <div>
              <div className={s.userName}>{user?.username || 'User'}</div>
              {user?.type && <span style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: 'var(--accent)', fontWeight: 600 }}>{user.type}</span>}
            </div>
          </div>
          <button className={s.menuItem} onClick={handleOpenMarkets}>
            <span className={s.menuItemIcon}><FiSliders /></span> Market Controls
          </button>
          <div className={s.divider} />
          <button className={s.menuItem} onClick={() => { setOpen(false); navigate('/settings'); }}>
            <span className={s.menuItemIcon}><FiSettings /></span> Settings
          </button>
          <button className={s.menuItem} onClick={() => { setOpen(false); navigate('/users'); }}>
            <span className={s.menuItemIcon}><FiUser /></span> Users
          </button>
          <button className={s.menuItem} onClick={() => { setOpen(false); }}>
            <span className={s.menuItemIcon}><FiHelpCircle /></span> Help
          </button>
          <div className={s.divider} />
          <button className={`${s.menuItem} ${s.menuItemDanger}`} onClick={() => { logout(); setOpen(false); }}>
            <span className={s.menuItemIcon}><FiLogOut /></span> Log Out
          </button>
        </div>
      )}

      {open && marketsOpen && (
        <div className={`${s.menu} ${s.menuRight} ${s.menuWide}`}>
          <div className={s.menuHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => setMarketsOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '2px 6px' }}>←</button>
              <span className={s.menuHeaderTitle}>Market Controls</span>
            </div>
          </div>

          {/* Disable / Enable All */}
          <div style={{ padding: '10px 16px' }}>
            <button
              onClick={allKill ? handleEnableAll : handleDisableAll}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid',
                borderColor: allKill ? 'rgba(115,201,145,0.3)' : 'rgba(224,108,117,0.3)',
                background: allKill ? 'rgba(115,201,145,0.06)' : 'rgba(224,108,117,0.06)',
                color: allKill ? '#73c991' : '#e06c75',
                fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center',
              }}
            >
              {allKill ? <FiToggleRight /> : <FiToggleLeft />}
              {allKill ? 'Enable All Markets' : 'Disable All Markets'}
            </button>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 4, textAlign: 'center' }}>
              Affects all pending &amp; started clasicos
            </div>
          </div>

          <div className={s.divider} />

          {/* Per-group toggles */}
          <div style={{ maxHeight: 320, overflowY: 'auto', padding: '4px 0' }}>
            <div style={{ padding: '6px 16px', fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Market Groups</div>
            {marketGroups.length === 0 && (
              <div style={{ padding: '8px 16px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Loading…</div>
            )}
            {marketGroups.map(g => {
              const code = g.code;
              const enabled = marketStates[code] !== false;
              return (
                <div
                  key={code}
                  className={s.menuItem}
                  onClick={() => handleToggleMarketGroup(code)}
                  style={{ justifyContent: 'space-between' }}
                >
                  <span style={{ fontSize: '0.82rem', opacity: enabled ? 1 : 0.5 }}>{g.label}</span>
                  <div style={{ width: 32, height: 18, borderRadius: 9, background: enabled ? '#73c991' : 'var(--border-color)', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: enabled ? 16 : 2, transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

